import { useState } from 'react';

import { createFileRoute } from '@tanstack/react-router';
import {
    AlertTriangle,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    FileText,
    MoreHorizontal,
    Settings,
    Star,
    Upload,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';

export const Route = createFileRoute('/lease/projects/$projectId/reports')({
    component: ProjectReportsPage,
});

type Section = 'favourites' | 'customBuilder' | 'complianceOverview';

function ProjectReportsPage() {
    const [activeTab, setActiveTab] = useState('standard');
    const [expandedSections, setExpandedSections] = useState({
        favourites: true,
        customBuilder: true,
        complianceOverview: true,
    });
    const [searchQuery, setSearchQuery] = useState('');

    const toggleSection = (section: Section) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const tabs = [
        { id: 'standard', label: 'Standard reports' },
        { id: 'custom', label: 'Custom reports' },
        { id: 'management', label: 'Management reports' },
        { id: 'export', label: 'Data export' },
        { id: 'dashboard', label: 'Dashboard centre' },
    ];

    const favouriteReports = [
        {
            name: 'AD - SB Compliance Check Report',
            icon: CheckCircle,
            starred: true,
            status: 'compliant',
        },
        {
            name: 'Open Issues Report',
            icon: AlertTriangle,
            starred: true,
            status: 'warning',
        },
        {
            name: 'Component Compliance Report',
            icon: Settings,
            starred: true,
            status: 'compliant',
        },
    ];

    const customReports = [
        { name: 'Aircraft Maintenance Status', icon: FileText },
        { name: 'Document Expiry Tracking', icon: Upload },
        { name: 'Lease Return Checklist Progress', icon: CheckCircle },
    ];

    const complianceReports = [
        {
            name: 'Status of Uploaded Documents Report',
            icon: Upload,
            starred: false,
            status: 'neutral',
        },
        {
            name: 'Missing Documents Report',
            icon: AlertTriangle,
            starred: true,
            status: 'warning',
        },
        {
            name: 'Airworthiness Directive Tracking',
            icon: FileText,
            starred: false,
            status: 'compliant',
        },
        {
            name: 'Service Bulletin Compliance',
            icon: Settings,
            starred: false,
            status: 'compliant',
        },
    ];

    return (
        <div
            className="min-h-screen bg-gray-50 p-6"
            data-testid="LeaseProjectReports"
        >
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-semibold text-gray-900 mb-8">
                    Reports
                </h1>

                {/* Navigation Tabs */}
                <div className="border-b border-gray-200 mb-6">
                    <nav className="flex space-x-8">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === tab.id
                                        ? 'border-blue-900 text-blue-900'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Search and Create Report */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center space-x-2 flex-1 max-w-md">
                        <Input
                            placeholder="Type report name here"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="flex-1"
                        />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="icon">
                                    <ChevronDown className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem>All Reports</DropdownMenuItem>
                                <DropdownMenuItem>
                                    Compliance Reports
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                    Document Reports
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                    Status Reports
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button className="bg-blue-900 hover:bg-blue-800 text-white">
                                Create new report
                                <ChevronDown className="ml-2 h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem>Standard Report</DropdownMenuItem>
                            <DropdownMenuItem>Custom Report</DropdownMenuItem>
                            <DropdownMenuItem>
                                Scheduled Report
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Report Sections */}
                <div className="space-y-6">
                    {/* Favourites Section */}
                    <Card>
                        <CardContent className="p-6">
                            <button
                                onClick={() => toggleSection('favourites')}
                                className="flex items-center space-x-2 mb-4 w-full text-left"
                            >
                                {expandedSections.favourites ? (
                                    <ChevronDown className="h-5 w-5 text-gray-500" />
                                ) : (
                                    <ChevronRight className="h-5 w-5 text-gray-500" />
                                )}
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Favourites
                                </h2>
                            </button>

                            {expandedSections.favourites && (
                                <div className="space-y-3">
                                    {favouriteReports.map((report, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between py-2 hover:bg-gray-50 rounded px-2"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <report.icon
                                                    className={`h-5 w-5 ${report.status === 'warning' ? 'text-orange-500' : 'text-green-500'}`}
                                                />
                                                <span className="text-gray-700">
                                                    {report.name}
                                                </span>
                                                {report.status ===
                                                    'warning' && (
                                                    <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                                                        Action Required
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Star className="h-4 w-4 text-orange-400 fill-current" />
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger
                                                        asChild
                                                    >
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                        >
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        <DropdownMenuItem>
                                                            Run Report
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem>
                                                            Schedule
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem>
                                                            Remove from
                                                            Favourites
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Custom Report Builder Section */}
                    <Card>
                        <CardContent className="p-6">
                            <button
                                onClick={() => toggleSection('customBuilder')}
                                className="flex items-center space-x-2 mb-4 w-full text-left"
                            >
                                {expandedSections.customBuilder ? (
                                    <ChevronDown className="h-5 w-5 text-gray-500" />
                                ) : (
                                    <ChevronRight className="h-5 w-5 text-gray-500" />
                                )}
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Custom report builder
                                </h2>
                            </button>

                            {expandedSections.customBuilder && (
                                <div>
                                    <p className="text-gray-600 mb-4">
                                        Check out a new way to build and view
                                        reports for aviation compliance.
                                        Customise these popular reports or start
                                        from scratch with{' '}
                                        <button className="text-blue-600 hover:underline">
                                            Create new report
                                        </button>
                                    </p>
                                    <div className="space-y-3">
                                        {customReports.map((report, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between py-2 hover:bg-gray-50 rounded px-2"
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <report.icon className="h-5 w-5 text-gray-400" />
                                                    <span className="text-gray-700">
                                                        {report.name}
                                                    </span>
                                                </div>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger
                                                        asChild
                                                    >
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                        >
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        <DropdownMenuItem>
                                                            Customize
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem>
                                                            Run Report
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem>
                                                            Add to Favourites
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Compliance Overview Section */}
                    <Card>
                        <CardContent className="p-6">
                            <button
                                onClick={() =>
                                    toggleSection('complianceOverview')
                                }
                                className="flex items-center space-x-2 mb-4 w-full text-left"
                            >
                                {expandedSections.complianceOverview ? (
                                    <ChevronDown className="h-5 w-5 text-gray-500" />
                                ) : (
                                    <ChevronRight className="h-5 w-5 text-gray-500" />
                                )}
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Compliance overview
                                </h2>
                            </button>

                            {expandedSections.complianceOverview && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {complianceReports.map((report, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between py-3 hover:bg-gray-50 rounded px-3 border border-gray-100"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <report.icon
                                                    className={`h-5 w-5 ${
                                                        report.status ===
                                                        'warning'
                                                            ? 'text-orange-500'
                                                            : report.status ===
                                                                'compliant'
                                                              ? 'text-green-500'
                                                              : 'text-gray-400'
                                                    }`}
                                                />
                                                <span className="text-gray-700 text-sm">
                                                    {report.name}
                                                </span>
                                                {report.status ===
                                                    'warning' && (
                                                    <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                                                        Missing Items
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {report.starred && (
                                                    <Star className="h-4 w-4 text-orange-400 fill-current" />
                                                )}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger
                                                        asChild
                                                    >
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                        >
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        <DropdownMenuItem>
                                                            Run Report
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem>
                                                            Schedule
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem>
                                                            {report.starred
                                                                ? 'Remove from Favourites'
                                                                : 'Add to Favourites'}
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
